import { useEffect, MutableRefObject } from 'react';
import { getDailyTime, getActiveSession, getDailyTimeSinceLastBreak } from '@features/admin/api/complianceServices';
import { getProgress, CourseProgress } from '@features/courses/api/progressServices';

const BREAK_REQUIRED_AFTER = 2 * 3600;

interface UseComplianceHydrationProps {
  userId: string | undefined;
  courseId: string;
  lessonId: string | null | undefined;
  sessionTimer: any;
  breakManager: any;
  sessionData: any;
  breakComplianceManager: any;
  setLessonTime: (time: number) => void;
  lessonTimeRef: MutableRefObject<number>;
  lastBreakTimeRef: MutableRefObject<number>;
  progressDataRef: MutableRefObject<CourseProgress | null>;
  setIsInitialized: (initialized: boolean) => void;
  setSessionTime: (time: number) => void;
  sessionTimeRef: MutableRefObject<number>;
  lastSyncedTimeRef: MutableRefObject<number>;
}

export const useComplianceHydration = ({
  userId,
  courseId,
  lessonId,
  sessionTimer,
  breakManager,
  sessionData,
  breakComplianceManager,
  setLessonTime,
  lessonTimeRef,
  lastBreakTimeRef,
  progressDataRef,
  setIsInitialized,
  setSessionTime,
  sessionTimeRef,
  lastSyncedTimeRef
}: UseComplianceHydrationProps) => {
  useEffect(() => {
    const initializeSession = async () => {
      if (userId && courseId) {
        const isTestMode = import.meta.env.VITE_TEST_MODE === 'true' || import.meta.env.MODE === 'test';
        if (!isTestMode) {
          setIsInitialized(false);
        }
        try {
          // 1. Load Daily Compliance Limits
          const dailyTime = await getDailyTime(userId, courseId);
          console.log('[TimerContext] initializeSession - getDailyTime returned:', dailyTime, 'for user:', userId, 'course:', courseId);
          sessionTimer.loadDailyTime(dailyTime);

          // 2. Load Total Course Progress
          const progressData = await getProgress(userId, courseId);
          if (progressData) {
            progressDataRef.current = progressData;
            sessionTimer.loadTotalCourseTime(progressData.totalInstructionSeconds || 0);

            // If we have an initial lessonId from props, initialize lessonTime
            if (lessonId && progressData.lessonProgress?.[lessonId]) {
              const persistentCredit = progressData.lessonProgress[lessonId].timeSpent || 0;
              setLessonTime(persistentCredit);
              lessonTimeRef.current = persistentCredit;
            }
          }

          // 3. SECURE HYDRATION: Check for active breaks in Firestore (interrupted sessions)
          // This prevents students from bypassing the 10-minute break via refresh.
          const activeSession = await getActiveSession(userId);
          console.warn('[TimerContext] initializeSession - activeSession fetched:', activeSession);
          if (activeSession) {
            // Count completed mandatory breaks in this session to hydrate lastBreakTimeRef
            const completedMandatoryBreaks = (activeSession.breaks || []).filter(
              (b: any) => b.reason === 'mandatory' && (b.status === 'completed' || !!b.endTime)
            );
            
            // Calculate continuous instruction offset from prior sessions today.
            const priorOffset = await getDailyTimeSinceLastBreak(userId, courseId, activeSession.id);

            if (completedMandatoryBreaks.length > 0) {
              lastBreakTimeRef.current = completedMandatoryBreaks.length * BREAK_REQUIRED_AFTER - priorOffset;
              console.log('[TimerContext] Hydrated lastBreakTimeRef from active session breaks with prior offset to:', lastBreakTimeRef.current);
            } else {
              // No completed breaks in active session yet.
              lastBreakTimeRef.current = -priorOffset;
              console.log('[TimerContext] Resumed active session: Hydrated lastBreakTimeRef to negative daily offset:', lastBreakTimeRef.current);
            }

            // ALWAYS hydrate session duration to prevent clock resets to 0 / 2h 0m
            const duration = activeSession.duration || 0;
            setSessionTime(duration);
            sessionTimeRef.current = duration;
            lastSyncedTimeRef.current = duration;
            console.warn('[TimerContext] Hydrated sessionTime, sessionTimeRef, and lastSyncedTimeRef to activeSession duration:', duration);

            // ALWAYS hydrate the session state to prevent bypass on reload
            sessionData.createSession(activeSession.id, {
              startTime: activeSession.startTime,
              courseId,
              lessonId: activeSession.lessonId || null,
              ipAddress: activeSession.ipAddress || '',
              lessonsAccessed: activeSession.lessonsAccessed || [],
              videoProgress: activeSession.videoProgress || null
            });

            if (activeSession.breaks && activeSession.breaks.length > 0) {
              const lastBreak = activeSession.breaks[activeSession.breaks.length - 1];
              console.warn('[TimerContext] initializeSession - lastBreak found in session:', lastBreak);

              // If the last break is still "active", check if the 10-minute timer has completed
              if (lastBreak.status === 'active') {
                // Restore timestamps from Firestore
                let breakStartMs: number;
                if (lastBreak.startTime?.toMillis) {
                  breakStartMs = lastBreak.startTime.toMillis();
                } else if (typeof lastBreak.startTime === 'string') {
                  breakStartMs = new Date(lastBreak.startTime).getTime();
                } else {
                  breakStartMs = lastBreak.startTime as number;
                }

                if (breakStartMs) {
                  const isMandatory = lastBreak.reason === 'mandatory';
                  const elapsedSeconds = Math.floor((Date.now() - breakStartMs) / 1000);

                  if (elapsedSeconds >= 600) {
                    // Auto-complete the break if 10 minutes have elapsed since it started!
                    console.warn('[TimerContext] Active break has fully elapsed (10+ minutes). Auto-completing it on mount.');
                    breakComplianceManager.endBreak().catch(err => {
                      console.error('[TimerContext] Failed to auto-complete elapsed break:', err);
                    });
                  } else {
                    console.warn('[TimerContext] Active break in progress. Hydrating break state.');
                    // Hydrate the break manager
                    breakManager.setIsOnBreak(true);
                    breakManager.setIsBreakMandatory(isMandatory);
                    breakManager.setBreakStartTime(breakStartMs);

                    // If it was a mandatory break, we need to inform the UI manager too
                    if (isMandatory) {
                      // Calculate remaining seconds for the UI countdown
                      const remainingSeconds = Math.max(0, 600 - elapsedSeconds);
                      breakComplianceManager.setBreakTime(remainingSeconds);
                    }

                    // Pause timer while on break
                    sessionTimer.pauseTimer();
                  }
                }
              }
            }
          } else {
            // New Session: Calculate negative offset from prior sessions today.
            const dailyTimeSinceLastBreak = await getDailyTimeSinceLastBreak(userId, courseId);
            lastBreakTimeRef.current = -dailyTimeSinceLastBreak;
            console.log('[TimerContext] New session: Hydrated lastBreakTimeRef to negative daily offset:', lastBreakTimeRef.current);

            // Clean up any stale break or session states from localStorage
            try {
              const keysToRemove: string[] = [];
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (
                  key.startsWith('fastrack_break_state_') || 
                  key.startsWith('fastrack_session_time_') ||
                  key.startsWith('fastrack_pvq_trigger_time_')
                )) {
                  keysToRemove.push(key);
                }
              }
              keysToRemove.forEach(key => localStorage.removeItem(key));
            } catch (e) {
              console.error('Failed to clean up stale localStorage keys:', e);
            }
          }
        } catch (err) {
          console.error('Error initializing session:', err);
        } finally {
          setIsInitialized(true);
        }
      } else {
        const isTestMode = import.meta.env.VITE_TEST_MODE === 'true' || import.meta.env.MODE === 'test';
        if (isTestMode) {
          setIsInitialized(true);
        } else {
          setIsInitialized(false);
        }
      }
    };

    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, courseId]);
};
